import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

/** Common Components */
import MolGrid from "../../../../components/molGrid/MolGrid";

/** Common Services & Data Files */
import SwalAlert from "../../../../services/swalService/SwalService";
import ToastService from "../../../../services/toastService/ToastService";

/** Configuration Files */
import { reportTemplateGridConfig } from "./reportTemplateList.grid.config";

/** RTK Query */
import { useDeleteReportTemplateMutation, useGetReportTemplateListMutation, useUpdateReportTemplateToggleMutation } from "../../../../app/services/reportTemplateAPI";
import PropTypes from "prop-types";
import { useDownloadCsvFileMutation } from "../../../../app/services/wordDocumentAPI";

const ReportTypesList = (props, ref) => {
  const molGridRef = useRef();

  const [dataSource, setDataSource] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [sortState, setSortState] = useState([]);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 100
  })	

  const [getReportTemplate, {
    isLoading: isReportTemplateListLoading,
    isSuccess: isReportTemplateListSuccess,
    data: reportTemplateListData
}] = useGetReportTemplateListMutation();

const [deleteReportTemplate, {
    isLoading: isReportTemplateDeleting,
    isSuccess: isReportTemplateDeleted,
    data: reportTemplateDeleteData
}] = useDeleteReportTemplateMutation();

const [updateReportTemplateToggle, {
    isLoading: isReportTemplateToggleUpdating,
    isSuccess: isReportTemplateToggleUpdated,
    data: reportTemplateUpdateToggledData,
}] = useUpdateReportTemplateToggleMutation();

const [downloadCsvFile, {
  isLoading: isCsvFetching,
  isSuccess: isCsvDownalodSucess,
  data: DownalodCsvData,
  isError: isDownloadCsvError,
  error: downloadCsvError
},] = useDownloadCsvFileMutation();

  const { confirm,error } = SwalAlert();

  const getReportTemplateList = (pageObject,field) => {
    const req = {
        pagination: {
            pageNumber: pageObject.pageNumber,
            pageSize: pageObject.pageSize
        },
        filters: {
            searchText: ""
        },
        sortString: field ? field : "",
    }
    getReportTemplate(req)
};

const handlePageChange = (pageObject) => {
  setPagination({
    pageNumber: pageObject?.pageNumber,
    pageSize: pageObject?.pageSize
  })
  getReportTemplateList(pageObject);
};

const sortData = (data, sortState) => {
      
  return [...data].sort((a, b) => {
      for (const sort of sortState) {
          const valA = a[sort.column] ?? '';
          const valB = b[sort.column] ?? '';
          const comparison = sort.direction === 'asc' 
              ? valA.toString().localeCompare(valB.toString())
              : valB.toString().localeCompare(valA.toString());
          if (comparison !== 0) return comparison;
      }
      return 0;
  });
};

const handleSortingChange = async(sortString,col) => {
  if (!sortString) return;    
  const newSortState = sortString
      .split(',')
      .map(item => {
          const [column, directionRaw] = item.trim().split(/\s+/); 
          const direction = directionRaw?.toLowerCase() === 'desc' ? 'desc' : 'asc';
          return { column, direction };
      })
      .filter(item => item.column); 
      const filtered = newSortState.filter(item => item.column === col.fieldName);
      setSortState(newSortState);
      let req = `${filtered[0].column} ${filtered[0].direction}`
      await getReportTemplateList(pagination, req)
      const orderData = await sortData(dataSource,filtered);
      // setDataSource(orderData);
};


const handleEditClick = (data) => {
  props.onEdit?.(data)
};

  const handleDeleteClick = (data) => {
    const reportTemplateID = data.reportTemplateID;
    confirm(
      "Delete?",
      "Are you sure you want to delete?",
      "Delete",
      "Cancel"
    ).then((confirmed) => {
      if (confirmed) {
        if (reportTemplateID) {
          deleteReportTemplate(reportTemplateID)
      }
      }
    });
  };

  const handleDownloadReport =(data)=>{
    const req = {
      reportTemplateID:data?.reportTemplateID,
      outFileName: data?.reportTemplateName,
    }
    downloadCsvFile(req)
  }

  const handleToggleChange = (fieldName, newRowData, rowIndex) => {
    const req = {
        status: newRowData.status ,
        reportTemplateID: newRowData.reportTemplateID,
    }
    updateReportTemplateToggle(req);
}

useEffect(() => {
  if (molGridRef.current) {
    getReportTemplateList(molGridRef.current.getDefaultPageObject());
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  useEffect(() => {
    if (isReportTemplateListSuccess && reportTemplateListData) {
        if (reportTemplateListData.dataSource) {
            setDataSource(reportTemplateListData.dataSource)
        }
        if (reportTemplateListData.totalRecord) {
            setTotalCount(reportTemplateListData.totalRecord)
        }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [isReportTemplateListSuccess, reportTemplateListData]);

useEffect(() => {
  if (isReportTemplateDeleted && reportTemplateDeleteData) {
      if (reportTemplateDeleteData.errorMessage) {
          ToastService.success(reportTemplateDeleteData.errorMessage);
          setDataSource([])
      };
      if (molGridRef.current) {
        getReportTemplateList(molGridRef.current.getDefaultPageObject());
      };
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [isReportTemplateDeleted, reportTemplateDeleteData]);

useEffect(() => {
  if (isReportTemplateToggleUpdated && reportTemplateUpdateToggledData) {
          ToastService.success(reportTemplateUpdateToggledData?.errorMessage);
          getReportTemplateList(molGridRef.current.getDefaultPageObject());
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [isReportTemplateToggleUpdated, reportTemplateUpdateToggledData]);

useEffect(() => {
  if (isCsvFetching) {
    return;
  }

  if (isDownloadCsvError) {
    const status = downloadCsvError?.message;

    const dialog = status === "Rejected"
      ? error("File Not Found")
      : error("Download Failed", "Something went wrong while downloading the file.");

    dialog.then(() => {
    });
    return;
  }

  if (!isCsvFetching && isCsvDownalodSucess && DownalodCsvData) {
    if (DownalodCsvData.blobUrl && DownalodCsvData.contentType) {
      const fileURL = DownalodCsvData.blobUrl;
      const link = document.createElement("a");
      link.href = fileURL;
      link.download = DownalodCsvData.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(fileURL);
    }
  }
}, [
  isCsvDownalodSucess,
  DownalodCsvData,
  isDownloadCsvError,
  downloadCsvError
]);

  const actionHandler = {
    EDIT: handleEditClick,
    DELETE: handleDeleteClick,
    DOWNLOAD_Report:handleDownloadReport
  };

  useImperativeHandle(
    ref,
    () => ({
      refreshGrid: () => {
        if (ref.current) {
          getReportTemplateList(molGridRef.current.getDefaultPageObject());
        } else {
          return null;
        }
      },
    }),
    [ref]
  );

  return (
    <MolGrid
      ref={molGridRef}
      configuration={reportTemplateGridConfig}
      multiSort={false}
      dataSource={dataSource}
      allowPagination={true}
      pagination={{
        totalCount: totalCount,
        pageSize: 20,
        currentPage: 1,
      }}
      onActionChange={actionHandler}
      onPageChange={handlePageChange}
      isLoading={isReportTemplateListLoading || isReportTemplateToggleUpdating}
      onSorting={handleSortingChange}
      onColumnChange={handleToggleChange}
      rowDoubleClick={handleEditClick}
    />
  );
};

ReportTypesList.prototype = {
  onEdit: PropTypes.bool
}


export default forwardRef(ReportTypesList);
